import { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { CampaignSubscriberWithJourney } from '@/shared/types/campaigns';
import { formatDate } from '@/util/dateUtils';

import { cleanContextString } from '../../utils';

interface OriginalDebtCardProps {
  subscriber: CampaignSubscriberWithJourney;
}

interface DebtEntry {
  amount?: number;
  currency?: string;
  due_date?: number | string;
  original_creditor?: string;
  debt_external_id?: string;
}

export const OriginalDebtCard = ({ subscriber }: OriginalDebtCardProps) => {
  const { t } = useTranslation();

  // Orchestrator PR #1839: `context.debts[]` (array) replaced `context.debt`
  // (singular). Slim entries on the list endpoint carry
  // `{debt_id, amount, currency, debt_external_id}`; full fields come from
  // GET /v1/contacts/:id/debts. Render one section per debt; if a subscriber
  // has none, fall back to a single N/A block so the card still anchors
  // the sidebar.
  const debts = useMemo<DebtEntry[]>(() => {
    const ctx = subscriber.context as Record<string, unknown> | undefined;
    const raw = ctx?.debts as DebtEntry[] | undefined;
    return Array.isArray(raw) && raw.length > 0 ? raw : [{}];
  }, [subscriber.context]);

  const showSlotLabels = debts.length > 1;

  const formatAmount = (debt: DebtEntry): string => {
    if (typeof debt.amount !== 'number') {
      return t('common.na');
    }
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: debt.currency || 'USD',
    }).format(debt.amount);
  };

  // Customer CSV uploads sometimes ship literal placeholder tokens ("null",
  // "none", "-", …); cleanContextString coerces those to undefined so the
  // N/A fallback fires instead of rendering the token verbatim (#4332).
  const formatCreditor = (debt: DebtEntry): string =>
    cleanContextString(debt.original_creditor) ?? t('common.na');

  // Pass the raw value (YYYY-MM-DD string or Unix-ms number) straight to
  // formatDate. parseTimestamp inside formatDate interprets date-only strings
  // as local midnight, so users west of UTC don't see the date shifted back a
  // day (which `new Date("YYYY-MM-DD")` would cause). Strings are cleaned
  // first so placeholder tokens like "null" become N/A (#4332).
  const formatDueDate = (debt: DebtEntry): string => {
    const raw = debt.due_date;
    const dueDate = typeof raw === 'number' ? raw : cleanContextString(raw);
    return dueDate ? formatDate(dueDate) : t('common.na');
  };

  return (
    <Card className="rounded-md bg-background pt-0 text-foreground shadow-none">
      <CardHeader className="flex items-center border-b pt-3 pb-2.5! pl-5">
        <CardTitle className="text-base leading-none">
          {t('campaigns.subscriber_journey.original_debt')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {debts.map((debt, index) => (
          <div key={debt.debt_external_id ?? `debt-${index}`} className="flex flex-col gap-4">
            {index > 0 && <Separator />}
            {showSlotLabels && (
              <span className="text-xs font-semibold tracking-wide text-foreground-muted uppercase">
                {t('campaigns.match_fields.debt_details_n', { 0: index + 1 })}
              </span>
            )}
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {t('campaigns.subscriber_journey.original_balance')}
              </span>
              <span className="text-sm font-semibold">{formatAmount(debt)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {t('campaigns.subscriber_journey.original_creditor')}
              </span>
              <span className="text-sm font-semibold">{formatCreditor(debt)}</span>
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs text-foreground-muted">
                {t('campaigns.subscriber_journey.due_date')}
              </span>
              <span className="text-sm font-semibold">{formatDueDate(debt)}</span>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};
